import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useServers } from '../servers/ServersContext';
import ServerIcon from '../servers/ServerIcon';
import JoinServerDialog from '../servers/JoinServerDialog';
import { lastChannel, serverUnread } from '../servers/format';

// Left column: Home (DMs), then the community servers the user joined, then "+" to join one
function RailButton({ active, unread, label, onClick, disabled, children, badge, square, ...rest }) {
  return (
    <div className="group relative flex w-full justify-center" {...rest}>
      {/* Active / unread / hover indicator */}
      <span
        className={`absolute left-0 top-1/2 w-1 -translate-y-1/2 rounded-r bg-white transition-all duration-200 ${
          active ? 'h-10' : disabled ? 'h-0' : unread ? 'h-2 group-hover:h-5' : 'h-0 group-hover:h-5'
        }`}
      />
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={onClick}
        disabled={disabled}
        className={`relative flex h-12 w-12 items-center justify-center overflow-visible border-0 p-0 font-display text-lg font-black transition-all duration-200 ${
          active
            ? `cursor-pointer rounded-lg ${square ? 'bg-transparent' : 'bg-brand text-white'}`
            : disabled
              ? 'cursor-not-allowed rounded-full bg-surface-2 text-text-dim'
              : `cursor-pointer rounded-full hover:rounded-lg ${square ? 'bg-transparent' : 'bg-surface-2 text-text-muted hover:bg-brand hover:text-white'}`
        }`}
      >
        {children}
        {badge > 0 && (
          <span className="absolute -bottom-0.5 -right-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full border-[3px] border-solid border-black bg-red px-1 text-[10px] font-extrabold text-white">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </button>
    </div>
  );
}

export default function ServerRail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { dmUnread, notifUnread } = useAuth();
  const { servers, state, reorder } = useServers();
  const [joinOpen, setJoinOpen] = useState(false);
  const [dragId, setDragId] = useState(null);
  const onHome = location.pathname === '/' || location.pathname.startsWith('/dm');
  const activeServer = location.pathname.match(/^\/servers\/([a-z0-9]{10})/)?.[1];

  const open = (id) => {
    const last = lastChannel(id);
    navigate(last ? `/servers/${id}/${last}` : `/servers/${id}`);
  };

  // Desktop: drag a server icon onto another to move it there
  const drop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const ids = servers.map(s => s.id).filter(id => id !== dragId);
    ids.splice(ids.indexOf(targetId), 0, dragId);
    reorder(ids);
  };

  return (
    <nav className="flex h-full w-rail shrink-0 flex-col items-center gap-2 overflow-y-auto overflow-x-hidden bg-black pb-[calc(12px_+_var(--safe-bottom))] pt-3" style={{ scrollbarWidth: 'none' }}>
      <RailButton active={onHome} label="Direct Messages" onClick={() => navigate('/dm')} badge={dmUnread + notifUnread}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </RailButton>

      <div className="h-0.5 w-8 shrink-0 rounded-full bg-surface-3" />

      {servers.map(s => {
        const st = state[s.id];
        const { any, mentions } = serverUnread(st?.unread);
        const isActive = activeServer === s.id;
        const name = st?.info?.name || s.name;
        return (
          <RailButton
            key={s.id}
            square
            active={isActive}
            unread={any && !isActive}
            label={name}
            badge={mentions}
            onClick={() => open(s.id)}
            draggable
            onDragStart={() => setDragId(s.id)}
            onDragEnd={() => setDragId(null)}
            onDragOver={e => { if (dragId) e.preventDefault(); }}
            onDrop={e => { e.preventDefault(); drop(s.id); setDragId(null); }}
            style={{ opacity: dragId === s.id ? 0.4 : 1 }}
          >
            <ServerIcon
              node={s}
              icon={st?.info?.icon}
              name={name}
              size={48}
              muted={st?.status === 'error'}
              className={`transition-all duration-200 ${isActive ? 'rounded-lg' : 'rounded-full group-hover:rounded-lg'}`}
            />
          </RailButton>
        );
      })}

      <RailButton label="Join a server" onClick={() => setJoinOpen(true)}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </RailButton>

      {joinOpen && <JoinServerDialog onClose={() => setJoinOpen(false)} />}
    </nav>
  );
}
